import { Navigate, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { lazy, Suspense } from 'react';
import type { UserRole } from '@recruitment/types';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Skeleton } from '@/components/ui/skeleton';
import LoginPage from '@/pages/auth/LoginPage';
import { useAuth } from '@/contexts/AuthContext';

// Lazy-load pages for code splitting

const CHUNK_RELOAD_KEY = 'internal_portal_chunk_reload_attempted';

function isChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /dynamically imported module|failed to fetch dynamically imported module|loading chunk|chunkloaderror|importing a module script failed/i.test(
    message,
  );
}

function readChunkReloadAttempted() {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';
  } catch {
    return false;
  }
}

function markChunkReloadAttempted() {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
  } catch {
    // Ignore storage failures; the browser reload is still the useful recovery.
  }
}

function clearChunkReloadAttempted() {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function lazyRoute<T extends React.ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await loader();
      clearChunkReloadAttempted();
      return mod;
    } catch (error) {
      const alreadyReloaded = readChunkReloadAttempted();
      if (isChunkLoadError(error) && !alreadyReloaded) {
        markChunkReloadAttempted();
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}

const DashboardPage = lazyRoute(
  () => import('@/pages/dashboard/DashboardPage'),
);
const ApplicationsPage = lazyRoute(
  () => import('@/pages/applications/ApplicationsPage'),
);
const ApplicationDetailPage = lazyRoute(
  () => import('@/pages/applications/ApplicationDetailPage'),
);

// Assessment pages
const AssessmentPanelsPage = lazyRoute(
  () => import('@/pages/assessments/PanelsPage'),
);
const PanelDetailPage = lazyRoute(
  () => import('@/pages/assessments/PanelDetailPage'),
);
const AssessmentTypesPage = lazyRoute(
  () => import('@/pages/assessments/AssessmentTypesPage'),
);
const AssessmentSessionsPage = lazyRoute(
  () => import('@/pages/assessments/SessionsPage'),
);
const PanelScheduleListPage = lazyRoute(
  () => import('@/pages/assessments/PanelScheduleListPage'),
);
const PanelRoomPage = lazyRoute(
  () => import('@/pages/assessments/PanelRoomPage'),
);
const PanelJobQuestionsPage = lazyRoute(
  () => import('@/pages/assessments/PanelJobQuestionsPage'),
);
const ScreeningPage = lazyRoute(
  () => import('@/pages/assessments/ScreeningPage'),
);
const ScoringPage = lazyRoute(() => import('@/pages/assessments/ScoringPage'));
const CandidateScoreSummaryPage = lazyRoute(
  () => import('@/pages/assessments/CandidateScoreSummaryPage'),
);
const CandidateProfilePage = lazyRoute(
  () => import('@/pages/assessments/CandidateProfilePage'),
);
const CandidateVerificationPage = lazyRoute(
  () => import('@/pages/assessments/CandidateVerificationPage'),
);
const AssessmentReportsPage = lazyRoute(
  () => import('@/pages/assessments/AssessmentReportsPage'),
);
const AssessmentBatchUploadPage = lazyRoute(
  () => import('@/pages/assessments/AssessmentBatchUploadPage'),
);
const AssessmentReviewCenterPage = lazyRoute(
  () => import('@/pages/assessments/AssessmentReviewCenterPage'),
);
const AiAssessmentReportPage = lazyRoute(
  () => import('@/pages/assessments/AiAssessmentReportPage'),
);
const ApplicationReportsPage = lazyRoute(
  () => import('@/pages/reports/ApplicationReportsPage'),
);
const HcmReviewReportsPage = lazyRoute(
  () => import('@/pages/reports/HcmReviewReportsPage'),
);
const VacancyAssessmentConfigPage = lazyRoute(
  () => import('@/pages/assessments/VacancyAssessmentConfigPage'),
);
const PanelRoleConfigPage = lazyRoute(
  () => import('@/pages/assessments/PanelRoleConfigPage'),
);
const PanelMemberSchedulePage = lazyRoute(
  () => import('@/pages/assessments/PanelMemberSchedulePage'),
);
const InterviewCandidatesPage = lazyRoute(
  () => import('@/pages/assessments/InterviewCandidatesPage'),
);

// Interview pages
const InterviewTimetablePage = lazyRoute(
  () => import('@/pages/interviews/TimetablePage'),
);
const DailyQuestionsPage = lazyRoute(
  () => import('@/pages/interviews/DailyQuestionsPage'),
);
const AttendancePage = lazyRoute(
  () => import('@/pages/interviews/AttendancePage'),
);

// Admin pages
const JobRolesPage = lazyRoute(() => import('@/pages/admin/JobRolesPage'));
const JobRoleApplicationsPage = lazyRoute(
  () => import('@/pages/admin/JobRoleApplicationsPage'),
);
const JobRoleDetailPage = lazyRoute(
  () => import('@/pages/admin/JobRoleDetailPage'),
);
const JobRolePreviewPage = lazyRoute(
  () => import('@/pages/admin/JobRolePreviewPage'),
);
const RecruitmentCyclesPage = lazyRoute(
  () => import('@/pages/admin/RecruitmentCyclesPage'),
);
const UsersPage = lazyRoute(() => import('@/pages/admin/UsersPage'));
const AdminsPage = lazyRoute(() => import('@/pages/admin/AdminsPage'));
const SettingsPage = lazyRoute(() => import('@/pages/admin/SettingsPage'));

const OAuthCompletePage = lazyRoute(
  () => import('@/pages/auth/OAuthCompletePage'),
);
const AdminSetupPage = lazyRoute(() => import('@/pages/auth/AdminSetupPage'));

function getDefaultRoute(
  hasRole: (...roles: UserRole[]) => boolean,
  hasPanelRole: () => boolean,
): string {
  if (hasRole('candidate_verifier') && !hasRole('super_admin'))
    return '/candidate-verification';
  if (hasRole('assessment_review_approver'))
    return '/admin/assessments/review-approval';
  if (hasRole('hcm_reviewer')) return '/admin/assessments/hcm-review-panels';
  if (hasRole('qa_qc')) return '/admin/assessments/qa-qc';
  if (hasPanelRole() && !hasRole('candidate_verifier'))
    return '/panel-sessions';
  if (hasRole('management', 'hcm_admin', 'hcm_support')) return '/';
  return '/admin/assessments/types';
}

function RoleRoute({
  roles,
  children,
}: {
  roles: UserRole[];
  children: React.ReactElement;
}) {
  const { hasRole, hasPanelRole } = useAuth();
  if (!hasRole(...roles)) {
    return <Navigate to={getDefaultRoute(hasRole, hasPanelRole)} replace />;
  }
  return children;
}

function PanelRoleRoute({ children }: { children: React.ReactElement }) {
  const { hasRole, hasPanelRole } = useAuth();
  const canAccess =
    hasRole('super_admin') ||
    (hasPanelRole() && !hasRole('candidate_verifier'));
  if (!canAccess) {
    return <Navigate to={getDefaultRoute(hasRole, hasPanelRole)} replace />;
  }
  return children;
}

const NotFound = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <p className="text-6xl font-bold text-muted-foreground/30">404</p>
    <h1 className="mt-4 text-xl font-semibold">Page not found</h1>
    <p className="mt-2 text-muted-foreground">
      The page you're looking for doesn't exist.
    </p>
  </div>
);

function PageLoader() {
  return (
    <div className="space-y-4 p-2">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-48" />
      <div className="mt-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

function AppInner() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/auth/complete"
          element={
            <Suspense fallback={<PageLoader />}>
              <OAuthCompletePage />
            </Suspense>
          }
        />
        <Route
          path="/admin-setup"
          element={
            <Suspense fallback={<PageLoader />}>
              <AdminSetupPage />
            </Suspense>
          }
        />

        <Route element={<DashboardLayout />}>
          <Route
            path="/"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'management',
                  'hcm_admin',
                  'hcm_support',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <DashboardPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/applications"
            element={
              <RoleRoute roles={['super_admin', 'system_admin', 'management']}>
                <Suspense fallback={<PageLoader />}>
                  <ApplicationsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/applications/:id"
            element={
              <RoleRoute roles={['super_admin', 'system_admin', 'management']}>
                <Suspense fallback={<PageLoader />}>
                  <ApplicationDetailPage />
                </Suspense>
              </RoleRoute>
            }
          />

          {/* Assessment routes (legacy paths kept as aliases) */}
          <Route
            path="/assessments/types"
            element={<Navigate to="/admin/assessments/types" replace />}
          />
          <Route
            path="/assessments/panels"
            element={<Navigate to="/admin/assessments/panels" replace />}
          />
          <Route
            path="/assessments/panels/:id"
            element={
              <Suspense fallback={<PageLoader />}>
                <PanelDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/assessments/sessions"
            element={<Navigate to="/admin/assessments/sessions" replace />}
          />
          <Route
            path="/assessments/screening"
            element={<Navigate to="/admin/assessments/screening" replace />}
          />
          <Route
            path="/assessments/scoring"
            element={<Navigate to="/admin/assessments/scoring" replace />}
          />
          <Route
            path="/assessments/reports"
            element={<Navigate to="/admin/assessments/reports" replace />}
          />

          {/* Interview routes (legacy paths kept as aliases) */}
          <Route
            path="/interviews/timetable"
            element={<Navigate to="/admin/assessments/timetable" replace />}
          />
          <Route
            path="/interviews/questions"
            element={<Navigate to="/admin/assessments/questions" replace />}
          />
          <Route
            path="/interviews/attendance"
            element={<Navigate to="/admin/assessments/attendance" replace />}
          />
          <Route
            path="/interviews/verification"
            element={<Navigate to="/admin/assessments/verification" replace />}
          />

          {/* Admin Assessment routes */}
          <Route
            path="/admin/assessments/types"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AssessmentTypesPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/panels"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AssessmentPanelsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/panels/:id"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <PanelDetailPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/sessions"
            element={
              <RoleRoute
                roles={[
                  'panel_member',
                  'panel_secretary',
                  'interviewer',
                  'secretary',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AssessmentSessionsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/panel-sessions"
            element={
              <PanelRoleRoute>
                <Suspense fallback={<PageLoader />}>
                  <PanelScheduleListPage />
                </Suspense>
              </PanelRoleRoute>
            }
          />
          <Route
            path="/panel-sessions/:panelId"
            element={
              <PanelRoleRoute>
                <Suspense fallback={<PageLoader />}>
                  <PanelRoomPage />
                </Suspense>
              </PanelRoleRoute>
            }
          />
          <Route
            path="/panel-sessions/:panelId/job-roles/:jobRoleId/questions"
            element={
              <PanelRoleRoute>
                <Suspense fallback={<PageLoader />}>
                  <PanelJobQuestionsPage />
                </Suspense>
              </PanelRoleRoute>
            }
          />
          <Route
            path="/panel-sessions/:panelId/candidates/:jobApplicationId"
            element={
              <PanelRoleRoute>
                <Suspense fallback={<PageLoader />}>
                  <CandidateProfilePage />
                </Suspense>
              </PanelRoleRoute>
            }
          />
          <Route
            path="/admin/assessments/screening"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <ScreeningPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/scoring"
            element={
              <PanelRoleRoute>
                <Suspense fallback={<PageLoader />}>
                  <ScoringPage />
                </Suspense>
              </PanelRoleRoute>
            }
          />
          <Route
            path="/admin/assessments/scoring/summary/:sessionId"
            element={
              <PanelRoleRoute>
                <Suspense fallback={<PageLoader />}>
                  <CandidateScoreSummaryPage />
                </Suspense>
              </PanelRoleRoute>
            }
          />
          <Route
            path="/admin/assessments/reports"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'management',
                  'hcm_admin',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AssessmentReportsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/review-center"
            element={
              <RoleRoute roles={['super_admin', 'system_admin', 'hcm_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AssessmentReviewCenterPage key="review-center" />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/hcm-review"
            element={
              <RoleRoute roles={['super_admin', 'system_admin', 'hcm_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AssessmentReviewCenterPage key="hcm" mode="hcm" />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/qa-qc"
            element={
              <RoleRoute
                roles={['super_admin', 'system_admin', 'hcm_admin', 'qa_qc']}
              >
                <Suspense fallback={<PageLoader />}>
                  <AssessmentReviewCenterPage key="qa" mode="qa" />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/hcm-review-panels"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'hcm_reviewer',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AssessmentReviewCenterPage key="panels" mode="panels" />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/review-approval"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'assessment_review_approver',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AssessmentReviewCenterPage key="approval" mode="approval" />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/ai-report/:candidateAssessmentId"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'qa_qc',
                  'management',
                  'assessment_review_approver',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AiAssessmentReportPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/reports/applications"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'management',
                  'hcm_admin',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <ApplicationReportsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/hcm-review-reports"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'management',
                  'hcm_admin',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <HcmReviewReportsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/vacancy-config"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <VacancyAssessmentConfigPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/interview-candidates"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <InterviewCandidatesPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/panel-role-config"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <PanelRoleConfigPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/panel-member-schedule"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <PanelMemberSchedulePage />
                </Suspense>
              </RoleRoute>
            }
          />

          {/* Admin interview/operations routes under assessment setup */}
          <Route
            path="/admin/assessments/batch-upload"
            element={
              <RoleRoute roles={['super_admin', 'system_admin', 'hcm_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AssessmentBatchUploadPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/timetable"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <InterviewTimetablePage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/questions"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <DailyQuestionsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/attendance"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <AttendancePage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/assessments/verification"
            element={
              <RoleRoute roles={['super_admin', 'candidate_verifier']}>
                <Suspense fallback={<PageLoader />}>
                  <CandidateVerificationPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/candidate-verification"
            element={
              <RoleRoute roles={['super_admin', 'candidate_verifier']}>
                <Suspense fallback={<PageLoader />}>
                  <CandidateVerificationPage />
                </Suspense>
              </RoleRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/job-roles"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <JobRolesPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/job-roles/new"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <JobRoleDetailPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/job-roles/:id/preview"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <JobRolePreviewPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/job-roles/:id/applications"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <JobRoleApplicationsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/job-roles/:id"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <JobRoleDetailPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/cycles"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hr',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <RecruitmentCyclesPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RoleRoute
                roles={[
                  'super_admin',
                  'system_admin',
                  'hcm_admin',
                  'admin',
                  'hcm_support',
                ]}
              >
                <Suspense fallback={<PageLoader />}>
                  <UsersPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/external-applicants"
            element={<Navigate to="/admin/users" replace />}
          />
          <Route
            path="/admin/admins"
            element={
              <RoleRoute roles={['super_admin', 'system_admin', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminsPage />
                </Suspense>
              </RoleRoute>
            }
          />
          <Route
            path="/admin/portal"
            element={
              <Navigate
                to="/admin/settings?section=portal-management"
                replace
              />
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RoleRoute roles={['super_admin', 'system_admin', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <SettingsPage />
                </Suspense>
              </RoleRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>

      <Toaster richColors position="top-right" />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}
